// Every change staff made, newest first, written as sentences. Opening an entry shows what changed
// as a before/after table (only the fields that differ, unless asked for all of them).
import { useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useInfiniteQuery } from '@tanstack/react-query';
import { ChevronDown, ScrollText } from 'lucide-react';
import { api } from '../lib/api';
import {
  EmptyState,
  ErrorNote,
  Initial,
  Mono,
  PageHeader,
  SearchInput,
  SkeletonRows,
  TimeAgo,
  useDebounced,
} from '../components/ui';

interface Entry {
  id: number;
  actor: string;
  action: string;
  targetType: string | null;
  targetId: string | null;
  before: unknown;
  after: unknown;
  ip: string | null;
  userAgent: string | null;
  createdAt: number;
}
interface Page {
  audit: Entry[];
  next: number | null;
  staff: { id: string; name: string }[];
  staffNames: Record<string, string>;
}

type Tone = 'neutral' | 'coin' | 'mint' | 'berry' | 'sky' | 'sun';
const ACTIONS: Record<string, { text: string; tone?: Tone }> = {
  'withdrawal.approve': { text: 'approved a cash-out', tone: 'mint' },
  'withdrawal.hold': { text: 'held a cash-out', tone: 'coin' },
  'withdrawal.reject': { text: 'rejected a cash-out', tone: 'berry' },
  'withdrawal.rereview': { text: 'sent a cash-out back to Claude' },
  'user.status': { text: 'changed an account status', tone: 'coin' },
  'user.adjust': { text: 'adjusted a balance', tone: 'coin' },
  'user.flag': { text: 'flagged an account', tone: 'berry' },
  'flag.confirmed': { text: 'confirmed a risk flag', tone: 'berry' },
  'flag.dismissed': { text: 'dismissed a risk flag' },
  'hold.release': { text: 'released held coins', tone: 'mint' },
  'hold.reverse': { text: 'reversed an old balance', tone: 'berry' },
  'setting.update': { text: 'changed a setting', tone: 'sun' },
  'provider.create': { text: 'added a network', tone: 'sky' },
  'provider.update': { text: 'edited a network', tone: 'sky' },
  'provider.key_viewed': { text: 'revealed a postback key', tone: 'sun' },
  'reversals.apply': { text: 'started a reversal batch', tone: 'berry' },
  'reversals.finished': { text: 'finished a reversal batch', tone: 'berry' },
  'reversals.continue': { text: 'continued someone else’s reversal batch', tone: 'berry' },
  'reversals.retry': { text: 'retried reversals that failed or waited' },
  'giftcards.sync': { text: 'started a catalog sync' },
  'giftcards.update': { text: 'edited a gift card' },
  'media.upload': { text: 'uploaded an image' },
  'support.reply': { text: 'replied to a ticket' },
  'support.status': { text: 'changed a ticket status' },
  'faq.item_created': { text: 'added an FAQ answer' },
  'faq.item_updated': { text: 'edited an FAQ answer' },
  'faq.item_published': { text: 'published an FAQ answer', tone: 'mint' },
  'faq.item_unpublished': { text: 'moved an FAQ answer to drafts', tone: 'sun' },
  'faq.item_deleted': { text: 'deleted an FAQ answer', tone: 'berry' },
  'faq.item_replaced': { text: 'replaced an FAQ answer with new wording', tone: 'mint' },
  'faq.group_created': { text: 'added an FAQ topic' },
  'faq.group_updated': { text: 'renamed an FAQ topic' },
  'faq.group_deleted': { text: 'deleted an FAQ topic', tone: 'berry' },
  'faq.reordered': { text: 'reordered the FAQ' },
  'faq.drafted': { text: 'asked Claude for FAQ drafts from a ticket' },
  'faq.seeded': { text: 'copied the FAQ into the database' },
  'staff.login': { text: 'signed in' },
  'staff.login_failed': { text: 'failed to sign in', tone: 'berry' },
  'staff.setup_requested': { text: 'asked for a setup link' },
  'staff.setup_started': { text: 'opened their setup link' },
  'staff.setup_completed': { text: 'finished account setup', tone: 'mint' },
  'staff.invited': { text: 'invited someone to the team', tone: 'sky' },
  'staff.invite_resent': { text: 'resent a team invite' },
  'staff.roles_changed': { text: 'changed someone’s roles', tone: 'sun' },
  'staff.disabled': { text: 'disabled a staff member', tone: 'berry' },
  'staff.enabled': { text: 'enabled a staff member', tone: 'mint' },
  'staff.reset': { text: 'reset someone’s password and 2FA', tone: 'sun' },
  'staff.removed': { text: 'removed someone from the team', tone: 'berry' },
  'staff.role_created': { text: 'created a role', tone: 'sky' },
  'staff.role_updated': { text: 'edited a role', tone: 'sun' },
  'staff.role_deleted': { text: 'deleted a role', tone: 'berry' },
};

const TONE_TEXT: Record<Tone, string> = {
  neutral: 'text-graphite',
  coin: 'font-semibold text-coin-ink',
  mint: 'font-semibold text-mint-deep',
  berry: 'font-semibold text-berry-deep',
  sky: 'font-semibold text-sky-800',
  sun: 'font-semibold text-[#9a6b00]',
};

const AREAS = [
  ['', 'Everything'],
  ['withdrawal', 'Cash-outs'],
  ['user', 'Accounts'],
  ['flag', 'Risk flags'],
  ['hold', 'Held coins'],
  ['support', 'Support'],
  ['faq', 'FAQ'],
  ['provider', 'Networks'],
  ['reversals', 'Reversals'],
  ['giftcards', 'Gift cards'],
  ['setting', 'Settings'],
  ['staff', 'Team and sign-ins'],
] as const;

function targetLink(type: string | null, id: string | null): string | null {
  if (!id) return null;
  if (type === 'user') return `/users/${id}`;
  if (type === 'withdrawal') return `/withdrawals?tab=all&id=${id}`;
  if (type === 'ticket') return `/support/${id}`;
  if (type === 'setting') return '/settings';
  if (type === 'provider') return '/providers';
  if (type === 'reversal_batch') return `/reversals?tab=history&batch=${id}`;
  if (type === 'gift_product') return '/giftcards';
  // Removed people too: the log outlives their time on the team.
  if (type === 'staff') return `/team?removed=1&person=${id}`;
  if (type === 'role') return `/team?view=roles&role=${id}`;
  if (type === 'faq_item') return `/faq?item=${id}`;
  if (type === 'faq_group' || type === 'faq') return '/faq';
  return null;
}

export function Audit() {
  const [params, setParams] = useSearchParams();
  const area = params.get('area') ?? '';
  const actor = params.get('actor') ?? '';
  const [q, setQ] = useState(params.get('target') ?? '');
  const target = useDebounced(q.trim());
  const set = (k: string, v: string) =>
    setParams((p) => (v ? p.set(k, v) : p.delete(k), p), { replace: true });
  const qs = (before: number | null) => {
    const s = new URLSearchParams();
    if (area) s.set('area', area);
    if (actor) s.set('actor', actor);
    if (target) s.set('target', target);
    if (before) s.set('before', String(before));
    return s.toString();
  };
  const { data, error, isLoading, refetch, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: ['audit', area, actor, target],
      queryFn: ({ pageParam }) => api<Page>(`/audit?${qs(pageParam)}`),
      initialPageParam: null as number | null,
      getNextPageParam: (last) => last.next,
      placeholderData: (p) => p,
    });
  const entries = data?.pages.flatMap((p) => p.audit) ?? [];
  const names = data?.pages[0]?.staffNames ?? {};
  const people = data?.pages[0]?.staff ?? [];
  const days = groupByDay(entries);

  return (
    <div className="space-y-5">
      <PageHeader
        title="Audit log"
        description="Every change made from this dashboard: who, what, from where. Entries can't be edited or deleted."
      />
      <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:items-center">
        <select
          className="input h-10 py-0 text-[14px] sm:w-44"
          value={area}
          onChange={(e) => set('area', e.target.value)}
          aria-label="Area"
        >
          {AREAS.map(([k, label]) => (
            <option key={k} value={k}>
              {label}
            </option>
          ))}
        </select>
        <select
          className="input h-10 py-0 text-[14px] sm:w-44"
          value={actor}
          onChange={(e) => set('actor', e.target.value)}
          aria-label="Staff member"
        >
          <option value="">All staff</option>
          {people.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
        <SearchInput
          value={q}
          onChange={(v) => {
            setQ(v);
            set('target', v.trim());
          }}
          placeholder="Exact id: usr_…, wdr_…, tkt_…"
          className="col-span-2 sm:w-72 [&_input]:h-10"
        />
        {(area || actor || q) && (
          <button
            type="button"
            className="col-span-2 justify-self-start rounded-[8px] px-2 py-2 text-[13px] font-semibold text-stone transition hover:text-ink sm:col-span-1"
            onClick={() => {
              setQ('');
              setParams(new URLSearchParams(), { replace: true });
            }}
          >
            Clear filters
          </button>
        )}
      </div>
      <ErrorNote error={error} onRetry={() => void refetch()} />
      {error && !data ? null : isLoading ? (
        <div className="card overflow-hidden">
          <SkeletonRows />
        </div>
      ) : entries.length === 0 ? (
        <div className="card">
          <EmptyState icon={ScrollText} title="Nothing logged">
            {area || actor || target
              ? 'Nothing matches these filters. Ids must match exactly.'
              : 'Staff actions appear here as soon as they happen.'}
          </EmptyState>
        </div>
      ) : (
        <>
          {days.map(([day, list]) => (
            <section key={day} aria-label={day}>
              <h2 className="mb-2 px-1 text-[13px] font-semibold text-stone">{day}</h2>
              <ul className="card divide-y divide-hairline overflow-hidden">
                {list.map((e) => (
                  <Row key={e.id} e={e} names={names} />
                ))}
              </ul>
            </section>
          ))}
          {hasNextPage && (
            <div className="text-center">
              <button
                className="btn-ghost"
                disabled={isFetchingNextPage}
                onClick={() => fetchNextPage()}
              >
                {isFetchingNextPage ? 'Loading…' : 'Show older entries'}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function groupByDay(entries: Entry[]): [string, Entry[]][] {
  const out = new Map<string, Entry[]>();
  const today = new Date().toDateString();
  const yesterday = new Date(Date.now() - 86_400_000).toDateString();
  for (const e of entries) {
    const d = new Date(e.createdAt);
    const key =
      d.toDateString() === today
        ? 'Today'
        : d.toDateString() === yesterday
          ? 'Yesterday'
          : d.toLocaleDateString('en-US', {
              weekday: 'short',
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            });
    out.set(key, [...(out.get(key) ?? []), e]);
  }
  return [...out];
}

const TARGET_LABEL: Record<string, string> = {
  user: 'Account',
  withdrawal: 'Cash-out',
  ticket: 'Ticket',
  setting: 'Setting',
  provider: 'Network',
  reversal_batch: 'Reversal batch',
  gift_product: 'Gift card',
  hold: 'Held coins',
  flag: 'Risk flag',
  staff: 'Staff',
};

function Row({ e, names }: { e: Entry; names: Record<string, string> }) {
  const [open, setOpen] = useState(false);
  const a = ACTIONS[e.action] ?? { text: e.action.replace(/[._]/g, ' ') };
  const who = names[e.actor] ?? e.actor;
  const link = targetLink(e.targetType, e.targetId);
  const summary = summarize(e);
  const time = new Date(e.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  return (
    <li>
      <button
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        className={`flex w-full items-center gap-3 px-4 py-3 text-left transition hover:bg-[#fbf8f3] focus-visible:-outline-offset-2 sm:px-5 ${open ? 'bg-[#fbf8f3]' : ''}`}
      >
        <Initial name={who} size={32} />
        <span className="min-w-0 flex-1">
          <span className="block text-[14px] leading-snug">
            <b className="font-bold">{who}</b>{' '}
            <span className={a.tone ? TONE_TEXT[a.tone] : 'text-graphite'}>{a.text}</span>
          </span>
          <span className="mt-0.5 flex min-w-0 gap-1.5 text-[13px] text-graphite">
            <span className="shrink-0 tabular-nums text-stone sm:hidden">{time}</span>
            {summary && (
              <>
                <span className="text-stone sm:hidden" aria-hidden="true">
                  ·
                </span>
                <span className="min-w-0 truncate">{summary}</span>
              </>
            )}
          </span>
        </span>
        <span className="hidden shrink-0 text-right text-[12px] leading-snug tabular-nums text-stone sm:block">
          <span className="block font-semibold text-graphite">{time}</span>
          <TimeAgo t={e.createdAt} />
        </span>
        <ChevronDown
          size={16}
          aria-hidden="true"
          className={`shrink-0 text-stone transition ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && (
        <div className="space-y-4 border-t border-hairline bg-[#fbf8f3] px-4 py-4 sm:px-5">
          <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-[13px] sm:flex sm:flex-wrap">
            {e.targetId && (
              <Meta label={TARGET_LABEL[e.targetType ?? ''] ?? e.targetType ?? 'Target'}>
                {link ? (
                  <Link
                    to={link}
                    className="font-semibold text-ink underline decoration-ink/25 underline-offset-2 transition hover:decoration-coin"
                  >
                    {e.targetId}
                  </Link>
                ) : (
                  <Mono>{e.targetId}</Mono>
                )}
              </Meta>
            )}
            <Meta label="When">
              {new Date(e.createdAt).toLocaleString('en-US', {
                dateStyle: 'medium',
                timeStyle: 'medium',
              })}
            </Meta>
            <Meta label="IP address">{e.ip ?? '—'}</Meta>
            {e.userAgent && (
              <Meta label="Browser" wide>
                <span className="block truncate font-normal text-graphite" title={e.userAgent}>
                  {e.userAgent}
                </span>
              </Meta>
            )}
          </dl>
          <Changes before={e.before} after={e.after} field={e.targetId} />
        </div>
      )}
    </li>
  );
}

function Meta({ label, wide, children }: { label: string; wide?: boolean; children: ReactNode }) {
  return (
    <div className={`min-w-0 ${wide ? 'col-span-2 sm:max-w-md sm:flex-1' : ''}`}>
      <dt className="text-[12px] font-semibold text-stone">{label}</dt>
      <dd className="mt-0.5 font-semibold tabular-nums">{children}</dd>
    </div>
  );
}

/** One line under the sentence: the reason, the new value, the amount. */
function summarize(e: Entry): string | null {
  const after = (e.after ?? {}) as Record<string, unknown>;
  const before = (e.before ?? {}) as Record<string, unknown>;
  if (e.action === 'setting.update') return `${e.targetId}: ${short(e.before)} → ${short(e.after)}`;
  if (e.action === 'user.adjust' && typeof after.coins === 'number')
    return `${after.coins > 0 ? '+' : ''}${after.coins.toLocaleString()} coins · ${String(after.reason ?? '')}`;
  if (e.action === 'user.status')
    return `${String(before.status ?? '?')} → ${String(after.status ?? '?')}${after.reason ? ` · ${String(after.reason)}` : ''}`;
  if (e.action === 'support.status') return `${String(before.status)} → ${String(after.status)}`;
  if (e.action === 'reversals.finished' && typeof after.coins === 'number')
    return `−${after.coins.toLocaleString()} coins · ${String(after.reversed ?? 0)} taken back`;
  if (typeof after.reason === 'string') return after.reason;
  if (typeof after.note === 'string' && after.note) return after.note;
  if (e.action.startsWith('withdrawal.') && after.ok === false)
    return 'Not applied: the cash-out had already moved on';
  return e.targetId;
}

function short(v: unknown): string {
  const s = typeof v === 'string' ? v : JSON.stringify(v);
  return s === undefined ? '—' : s.length > 60 ? `${s.slice(0, 57)}…` : s;
}

/** Nested objects become dotted paths so a provider edit shows "postback.auth.ipAllowlist". */
function flatten(v: unknown, prefix = '', out: Record<string, string> = {}, depth = 0) {
  if (v && typeof v === 'object' && !Array.isArray(v) && depth < 5) {
    for (const [k, x] of Object.entries(v))
      flatten(x, prefix ? `${prefix}.${k}` : k, out, depth + 1);
  } else if (prefix) out[prefix] = v === undefined ? '—' : JSON.stringify(v);
  return out;
}

/** A recorded value as a person would read it: strings without quotes, short lists joined. */
function pretty(json: string | undefined): string {
  if (json === undefined || json === '—') return '—';
  try {
    const v: unknown = JSON.parse(json);
    if (v === null) return 'empty';
    if (typeof v === 'string') return v === '' ? 'empty' : v;
    if (Array.isArray(v) && v.every((x) => x === null || typeof x !== 'object'))
      return v.length ? v.join(', ') : 'none';
    return json;
  } catch {
    return json;
  }
}

function Changes({
  before,
  after,
  field,
}: {
  before: unknown;
  after: unknown;
  field: string | null;
}) {
  const [all, setAll] = useState(false);
  if (before == null && after == null)
    return <p className="text-[13px] text-stone">No details recorded for this entry.</p>;
  const scalar = (x: unknown) => x === null || typeof x !== 'object' || Array.isArray(x);
  // A single value (a setting) reads as one row named after what changed.
  const b = scalar(before) && scalar(after) ? flatten({ v: before }) : flatten(before);
  const a = scalar(before) && scalar(after) ? flatten({ v: after }) : flatten(after);
  const single = scalar(before) && scalar(after);
  const keys = [...new Set([...Object.keys(b), ...Object.keys(a)])];
  const changed = keys.filter((k) => b[k] !== a[k]);
  const shown = all ? keys : changed;
  return (
    <div>
      <div className="overflow-hidden rounded-[12px] border border-hairline bg-paper text-[13px]">
        <div className="hidden grid-cols-[minmax(0,30%)_minmax(0,1fr)_minmax(0,1fr)] gap-4 border-b border-hairline bg-canvas/70 px-3.5 py-2 text-[12px] font-semibold text-stone sm:grid">
          <span>Field</span>
          <span>Before</span>
          <span>After</span>
        </div>
        {shown.length === 0 ? (
          <p className="px-3.5 py-2.5 text-stone">No field changed.</p>
        ) : (
          <ul className="divide-y divide-hairline">
            {shown.map((k) => {
              const diff = b[k] !== a[k];
              const name = single ? (field ?? 'Value') : k;
              return (
                <li
                  key={k}
                  className="grid gap-1 px-3.5 py-2.5 sm:grid-cols-[minmax(0,30%)_minmax(0,1fr)_minmax(0,1fr)] sm:gap-4"
                >
                  <span className="break-words font-mono text-[12px] text-graphite sm:pt-px">
                    {name}
                  </span>
                  <span className="min-w-0 text-stone [overflow-wrap:anywhere]">
                    <span className="mr-1.5 text-[11px] font-semibold sm:hidden">Before</span>
                    <span
                      className={
                        diff && b[k] !== undefined ? 'line-through decoration-berry/60' : ''
                      }
                    >
                      {pretty(b[k])}
                    </span>
                  </span>
                  <span
                    className={`min-w-0 [overflow-wrap:anywhere] ${diff ? 'font-semibold text-ink' : 'text-stone'}`}
                  >
                    <span className="mr-1.5 text-[11px] font-semibold text-stone sm:hidden">
                      After
                    </span>
                    {pretty(a[k])}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </div>
      {keys.length > changed.length && (
        <button
          className="mt-1 rounded-[8px] px-1 py-1.5 text-[12px] font-semibold text-stone transition hover:text-ink"
          onClick={() => setAll(!all)}
        >
          {all ? 'Show only what changed' : `Show all ${keys.length} fields`}
        </button>
      )}
    </div>
  );
}
